/* Mobile product-rail image look-ahead. Native lazySizes/RIAS owns responsive image loading. */
(() => {
 'use strict';
 if(window.RudcaRailImageLookahead)return;
 const railSelector='[data-recommendation-list],.rudca-discovery__list,.product-list--scrollable';
 const imageSelector='img.product-item__primary-image';
 const excluded='rudca-photo-scroll,[data-photo-item],.product-item__secondary-image';
 const limit=4,queueLimit=32,timeout=8000;
 const rails=new Map(),owners=new WeakMap(),attempted=new WeakSet(),queue=new Map(),flights=new Map();
 const mobile=window.matchMedia?.('(max-width: 640px)');
 const connection=navigator.connection||navigator.mozConnection||navigator.webkitConnection;
 let supported=typeof window.IntersectionObserver==='function',paused=false,outer=null,viewport=null,mutations=null,inView=new WeakSet(),visibilityKnown=new WeakSet(),draining=false,pumpPending=false,started=0,unveiled=0;
 const conservative=()=>Boolean(connection?.saveData||/^(slow-)?2g$/.test(connection?.effectiveType||''));
 const runtime=()=>typeof window.lazySizes?.loader?.unveil==='function'?window.lazySizes:null;
 const cfg=()=>runtime()?.cfg||{};
 const lazyClass=()=>cfg().lazyClass||'lazyload';
 function primary(img){return img instanceof HTMLImageElement&&img.matches(imageSelector)&&!img.closest(excluded);}
 function pending(img){
  const c=cfg();
  return img.classList.contains(c.lazyClass||'lazyload')&&!img._lazyRace&&!img.classList.contains(c.loadingClass||'lazyloading')&&!img.classList.contains(c.loadedClass||'lazyloaded')&&!img.classList.contains(c.errorClass||'lazyerror')&&Boolean(img.getAttribute(c.srcAttr||'data-src')||img.getAttribute(c.srcsetAttr||'data-srcset'));
 }
 function current(img,state){return !paused&&state.active&&state.rail.isConnected&&img.isConnected&&primary(img)&&owners.get(img)===state&&img.closest(railSelector)===state.rail&&state.near.has(img);}
 function visible(img){
  if(img.closest('[hidden],[aria-hidden="true"]'))return false;
  const style=getComputedStyle(img);
  return style.display!=='none'&&style.visibility!=='hidden'&&style.visibility!=='collapse';
 }
 function forget(img,state){
  queue.delete(img);state.near.delete(img);state.images.delete(img);state.observer?.unobserve(img);viewport?.unobserve(img);inView.delete(img);visibilityKnown.delete(img);
  if(owners.get(img)===state)owners.delete(img);
 }
 function schedulePump(){
  if(pumpPending||paused)return;
  pumpPending=true;Promise.resolve().then(()=>{pumpPending=false;pump();});
 }
 function startImage(img,state,native){
  let timer,nativeIssued=false,done=false;
  const finish=reason=>{
   if(done)return;done=true;clearTimeout(timer);
   for(const type of ['load','error','lazyloaded'])img.removeEventListener(type,onEvent);
   flights.delete(img);forget(img,state);
   // Interrupted first-source loads have not invoked native unveil yet. Re-arm
   // only these cancellations; errors/timeouts and issued native work stay deduped.
   if(reason==='cancel'&&!nativeIssued){
    attempted.delete(img);
    if(!paused&&outer&&img.isConnected&&rails.get(state.rail)===state&&pending(img))addImage(img,state);
   }
   schedulePump();
  };
  const unveil=()=>{
   if(!current(img,state)||!visible(img)){finish('cancel');return;}
   if(!pending(img)){finish();return;}
   nativeIssued=true;unveiled++;
   try{native.loader.unveil(img);}catch(_){finish();}
  };
  const onEvent=event=>{
   if(event.type==='error'||event.type==='lazyloaded'){finish();return;}
   if(img._lazyRace)return;
   // Some source-rendered cards already have a native-lazy src. Its first load
   // must finish before lazySizes accepts unveil and applies responsive sizing.
   if(!nativeIssued&&pending(img)&&img.complete&&img.naturalWidth>0){unveil();return;}
   if(!img.classList.contains(lazyClass())||nativeIssued)finish();
  };
  attempted.add(img);flights.set(img,{state,finish});started++;
  for(const type of ['load','error','lazyloaded'])img.addEventListener(type,onEvent);
  timer=setTimeout(finish,timeout); // Capacity only: never declare a slow image missing.
  const initialSourcePending=Boolean((img.getAttribute('src')||img.getAttribute('srcset'))&&!img.complete);
  img.setAttribute('loading','eager');
  if(!initialSourcePending)unveil();
 }
 function fillQueue(){
  queue.clear();
  // Await the visibility observer's first delivery before choosing between
  // visible images, visible rails and buffers; no scroll-time layout reads.
  for(let priority=2;priority>=0;priority--)for(const state of rails.values()){
   if(!state.active)continue;
   for(const img of state.near){
    if(queue.size>=queueLimit)return;
    if(attempted.has(img)||!visibilityKnown.has(img)||(inView.has(img)?2:inView.has(state.rail)?1:0)!==priority)continue;
    if(!current(img,state)||!pending(img)){forget(img,state);continue;}
    queue.set(img,state);
   }
  }
 }
 function pump(){
  if(draining||paused||!outer||!mobile?.matches||conservative())return;
  const native=runtime();
  fillQueue();if(!native)return;
  draining=true;
  try{
   while(flights.size<limit){
    fillQueue();const next=queue.entries().next();if(next.done)break;
    const [img,state]=next.value;queue.delete(img);
    if(attempted.has(img)||!current(img,state)||!pending(img)){if(!flights.has(img))forget(img,state);continue;}
    if(!visible(img)){state.near.delete(img);viewport?.unobserve(img);inView.delete(img);visibilityKnown.delete(img);continue;}
    startImage(img,state,native);
   }
  }finally{draining=false;}
 }
 function deactivate(state){
  state.active=false;state.observer?.disconnect();state.observer=null;
  for(const img of state.near){viewport?.unobserve(img);inView.delete(img);visibilityKnown.delete(img);}state.near.clear();
  for(const [img,owner]of queue)if(owner===state)queue.delete(img);
 }
 function activate(state){
  if(state.active)return;state.active=true;
  const observer=new IntersectionObserver(entries=>{
   if(paused||!state.active||state.observer!==observer)return;
   for(const entry of entries){
    const img=entry.target;if(owners.get(img)!==state)continue;
    const box=entry.boundingClientRect;
    if(entry.isIntersecting&&box.width>0&&box.height>0){state.near.add(img);viewport?.observe(img);}
    else{state.near.delete(img);queue.delete(img);viewport?.unobserve(img);inView.delete(img);visibilityKnown.delete(img);}
   }
   pump();
  },{root:state.rail,rootMargin:'0px 125%',threshold:0});
  state.observer=observer;for(const img of state.images)observer.observe(img);
 }
 function addImage(img,state){
  if(!primary(img)||img.closest(railSelector)!==state.rail||attempted.has(img)||owners.get(img)===state)return;
  const previous=owners.get(img);if(previous)forget(img,previous);
  owners.set(img,state);state.images.add(img);state.observer?.observe(img);
 }
 function addRail(rail){
  if(!rail.isConnected||rail.closest('rudca-photo-scroll,[data-photo-item]'))return;
  let state=rails.get(rail);
  if(!state){state={rail,active:false,observer:null,images:new Set(),near:new Set()};rails.set(rail,state);outer.observe(rail);viewport?.observe(rail);}
  rail.querySelectorAll(imageSelector).forEach(img=>addImage(img,state));
 }
 function scanNode(node){
  if(node?.nodeType!==1||!node.isConnected)return;
  if(node.matches(railSelector))addRail(node);
  node.querySelectorAll(railSelector).forEach(addRail);
  const rail=node.closest(railSelector),state=rails.get(rail);
  if(!state)return;
  if(node.matches(imageSelector))addImage(node,state);
  node.querySelectorAll(imageSelector).forEach(img=>addImage(img,state));
 }
 function prune(){
  for(const [rail,state]of rails){
   if(!rail.isConnected){
    deactivate(state);outer?.unobserve(rail);viewport?.unobserve(rail);inView.delete(rail);
    for(const img of state.images)forget(img,state);rails.delete(rail);
   }else for(const img of state.images)if(!img.isConnected||img.closest(railSelector)!==rail)forget(img,state);
  }
  for(const [img,flight]of flights)if(!img.isConnected||!flight.state.rail.isConnected||img.closest(railSelector)!==flight.state.rail)flight.finish('cancel');
 }
 function scan(node=document.documentElement){if(!outer||paused)return;prune();scanNode(node);pump();}
 function stop(){
  outer?.disconnect();outer=null;viewport?.disconnect();viewport=null;inView=new WeakSet();visibilityKnown=new WeakSet();mutations?.disconnect();mutations=null;queue.clear();
  for(const state of rails.values()){deactivate(state);for(const img of state.images)forget(img,state);}
  for(const flight of flights.values())flight.finish('cancel');
  rails.clear();
 }
 function wake(){
  if(paused||!supported||!mobile?.matches||conservative())return;
  if(outer){pump();return;}
  try{
   const visibleObserver=new IntersectionObserver(entries=>{
    if(paused||viewport!==visibleObserver)return;
    for(const entry of entries){
     const target=entry.target,state=owners.get(target),box=entry.boundingClientRect;
     if(!rails.has(target)&&(!state?.active||!state.near.has(target)))continue;
     visibilityKnown.add(target);
     if(entry.isIntersecting&&box.width>0&&box.height>0)inView.add(target);else inView.delete(target);
    }
    pump();
   },{root:null,rootMargin:'0px',threshold:0});
   viewport=visibleObserver;
   const observer=new IntersectionObserver(entries=>{
    if(paused||outer!==observer)return;
    for(const entry of entries){
     const state=rails.get(entry.target);if(!state)continue;
     const box=entry.boundingClientRect;
     if(entry.isIntersecting&&box.width>0&&box.height>0&&state.rail.isConnected)activate(state);else deactivate(state);
    }
    pump();
   },{root:null,rootMargin:'800px 0px',threshold:0});
   outer=observer;
   mutations=new MutationObserver(records=>{
    let removed=false;
    const added=new Set();
    for(const record of records){
     if(record.removedNodes.length)removed=true;
     for(const node of record.addedNodes)if(node.nodeType===1)added.add(node);
    }
    if(removed)prune();
    // Inspect only newly inserted subtrees, never the document on each mutation.
    for(const node of added)scanNode(node);
    pump();
   });
   mutations.observe(document.documentElement,{subtree:true,childList:true});
   scanNode(document.documentElement);pump();
  }catch(_){supported=false;stop();} // Unsupported observers leave the source loader in charge.
 }
 document.addEventListener('DOMContentLoaded',wake);
 window.addEventListener('load',wake);
 window.addEventListener('pagehide',()=>{paused=true;stop();});
 window.addEventListener('pageshow',()=>{paused=false;wake();});
 const policyChanged=()=>{stop();wake();};
 connection?.addEventListener?.('change',policyChanged);
 if(mobile?.addEventListener)mobile.addEventListener('change',policyChanged);else mobile?.addListener?.(policyChanged);
 window.RudcaRailImageLookahead=Object.freeze({installed:true,version:'25-native',scan,snapshot:()=>({supported,paused,mobile:Boolean(mobile?.matches),conservative:conservative(),rails:rails.size,activeRails:[...rails.values()].filter(state=>state.active).length,queued:queue.size,inFlight:flights.size,started,unveiled,maxConcurrent:limit,maxQueued:queueLimit,timeout})});
 wake();
})();
